import React, { useCallback, useMemo } from 'react';

import {
  getIsWorkflowChartDateDimension,
  WORKFLOW_CHART_DIMENSIONS,
  WORKFLOW_CHART_EMPTY_BUCKET_KEYS,
  WORKFLOW_CHART_HIDDEN_SERIES_KEY,
  WORKFLOW_CHART_MEASURE_LABELS,
  WORKFLOW_CHART_TYPES,
} from '@aline/common';
import type { WorkflowChartType } from '@aline/common';
import { useTheme } from '@mui/material';
import { BarPlot } from '@mui/x-charts-premium/BarChart';
import { ChartsDataProviderPro } from '@mui/x-charts-premium/ChartsDataProviderPro';
import type { ChartsDataProviderProProps } from '@mui/x-charts-premium/ChartsDataProviderPro';
import { ChartsSurface } from '@mui/x-charts-premium/ChartsSurface';
import { ChartsWrapper } from '@mui/x-charts-premium/ChartsWrapper';
import { ChartsXAxis } from '@mui/x-charts-premium/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts-premium/ChartsYAxis';
import { rainbowSurgePalette } from '@mui/x-charts-premium/colorPalettes';
import type { ChartPremiumApi } from '@mui/x-charts-premium/context';
import { useXScale, useYScale } from '@mui/x-charts-premium/hooks';
import { LineChartPro } from '@mui/x-charts-premium/LineChartPro';
import type {
  BarItemIdentifier,
  BarSeriesType,
  LineItemIdentifier,
  LineSeriesType,
  PieItemIdentifier,
} from '@mui/x-charts-premium/models';
import { PieChartPro } from '@mui/x-charts-premium/PieChartPro';
import { getPriorityColor } from 'src/components/workflow/workflow-list/cells/WorkflowPriority';
import { Apollo } from 'src/gql/generated';

import { formatInsightChartNumber, getInsightValueFormatter } from './insight-chart-series';
import type { InsightChartModel } from './insight-chart-series';
import { InsightAxisTooltip, InsightItemTooltip } from './InsightChartTooltip';
import type { InsightChartDataPoint } from './workflow-drilldown';

export const INSIGHT_CHART_HEIGHT_PX = 280;
/** Axes size themselves (`'auto'`), so the margin only keeps the plot off the card edge. */
const INSIGHT_CHART_MARGIN = { bottom: 8, left: 8, right: 16, top: 24 };
const INSIGHT_HORIZONTAL_AXIS_WIDTH_PX = 112;
/**
 * Below ~36° the tilted labels of a dense axis still overlap each other, so this is
 * about as shallow — and as short — as a rotated axis can get.
 */
const INSIGHT_ROTATED_TICK_LABEL_STYLE = { angle: -35, textAnchor: 'end' } as const;
/** Total characters a bottom axis can lay out side by side before labels collide. */
const INSIGHT_CATEGORY_LABEL_CHAR_BUDGET = 60;
/**
 * Tilted labels are given a fixed slice of the card rather than `'auto'`: an
 * unbounded axis lets one long category name flatten the plot to nothing, and
 * hovering a bar still reveals whatever the axis had to truncate.
 */
const INSIGHT_ROTATED_AXIS_HEIGHT_PX = 120;
const INSIGHT_ROTATED_CHART_HEIGHT_PX = 340;
/** The "total across all secondary keys" overlay line, which is not drillable. */
const INSIGHT_CHART_TOTAL_SERIES_ID = '__full_total__';
const INSIGHT_CHART_TOTAL_SERIES_COLOR = '#607D8B';
/** The server's "Other" remainder series, and the fallback once the palette runs out. */
const INSIGHT_CHART_HIDDEN_SERIES_COLOR = '#B0BEC5';
const INSIGHT_AXIS_TOOLTIP_SLOTS = { tooltip: InsightAxisTooltip };
const INSIGHT_ITEM_TOOLTIP_SLOTS = { tooltip: InsightItemTooltip };
const INSIGHT_AXIS_VALUES: { auto: 'auto'; band: 'band'; point: 'point' } = {
  auto: 'auto',
  band: 'band',
  point: 'point',
};
const INSIGHT_PIE_HIGHLIGHT_SCOPE: { fade: 'global'; highlight: 'item' } = {
  fade: 'global',
  highlight: 'item',
};
const PRIORITY_SERIES_COLORS: Record<string, string> = {
  [Apollo.WorkflowPriority.High]: getPriorityColor(Apollo.WorkflowPriority.High),
  [Apollo.WorkflowPriority.Low]: getPriorityColor(Apollo.WorkflowPriority.Low),
  [Apollo.WorkflowPriority.Medium]: getPriorityColor(Apollo.WorkflowPriority.Medium),
  [Apollo.WorkflowPriority.Urgent]: getPriorityColor(Apollo.WorkflowPriority.Urgent),
  [WORKFLOW_CHART_EMPTY_BUCKET_KEYS.priority]: getPriorityColor(null),
};

export type InsightChartApiRef = React.RefObject<ChartPremiumApi<'composition'> | undefined>;

type InsightChartPlotProps = {
  apiRef: InsightChartApiRef;
  model: InsightChartModel;
  skipAnimation: boolean;
  colorDimension?: string;
  onSelectDataPoint?: (point: InsightChartDataPoint) => void;
  /** Axis title naming what the category axis plots. */
  primaryLabel?: string;
  /** Tilt category tick labels that cannot sit side by side at full width. */
  rotateCategoryLabels?: boolean;
  /** Plural secondary Dimension name, used to describe the full-cohort total. */
  secondaryLabel?: string;
};

type InsightChartPlotShellProps = {
  apiRef: InsightChartApiRef;
  children: React.ReactNode;
  height: number;
  series: (BarSeriesType | LineSeriesType)[];
  skipAnimation: boolean;
  xAxis: NonNullable<ChartsDataProviderProProps['xAxis']>;
  yAxis: NonNullable<ChartsDataProviderProProps['yAxis']>;
};

/**
 * MUI shortens band tick labels to fit their band and renders nothing when even an
 * ellipsis will not fit. A date axis is ordered, so dropping labels still leaves a
 * readable sequence, but category names are identities — tilt those instead and let
 * the axis grow to fit them.
 */
export function getShouldRotateCategoryLabels(options: {
  dimension?: string;
  labels?: string[];
}): boolean {
  const { dimension, labels } = options;
  if (!labels?.length) return false;
  if (dimension && getIsWorkflowChartDateDimension(dimension)) return false;
  const characters = labels.reduce((total, label) => total + label.length, 0);
  return characters > INSIGHT_CATEGORY_LABEL_CHAR_BUDGET;
}

function getCategoryAxisLabelConfig(options: { label?: string; rotate?: boolean }) {
  const { label, rotate } = options;
  if (!rotate) return { height: 'auto' as const, label };
  return {
    height: INSIGHT_ROTATED_AXIS_HEIGHT_PX,
    label,
    tickLabelStyle: INSIGHT_ROTATED_TICK_LABEL_STYLE,
  };
}

function getChartHeight(rotateCategoryLabels?: boolean): number {
  return rotateCategoryLabels ? INSIGHT_ROTATED_CHART_HEIGHT_PX : INSIGHT_CHART_HEIGHT_PX;
}

/**
 * Composed bar plots cannot use `ChartsContainerPro`: that helper never mounts
 * `ChartsWrapper`, so `chartRootRef` stays empty and `exportAsImage` returns
 * without downloading. Line/Pie Pro charts already wrap themselves.
 */
function InsightChartPlotShell({
  apiRef,
  children,
  height,
  series,
  skipAnimation,
  xAxis,
  yAxis,
}: InsightChartPlotShellProps) {
  return (
    <ChartsDataProviderPro
      apiRef={apiRef}
      height={height}
      margin={INSIGHT_CHART_MARGIN}
      series={series}
      skipAnimation={skipAnimation}
      xAxis={xAxis}
      yAxis={yAxis}
    >
      <ChartsWrapper hideLegend>
        <ChartsSurface>{children}</ChartsSurface>
      </ChartsWrapper>
    </ChartsDataProviderPro>
  );
}

function InsightColumnPlot({
  apiRef,
  colorDimension,
  model,
  onSelectDataPoint,
  primaryLabel,
  rotateCategoryLabels,
  secondaryLabel,
  skipAnimation,
}: InsightChartPlotProps) {
  return (
    <InsightBarsPlot
      apiRef={apiRef}
      colorDimension={colorDimension}
      layout="vertical"
      model={model}
      primaryLabel={primaryLabel}
      rotateCategoryLabels={rotateCategoryLabels}
      secondaryLabel={secondaryLabel}
      skipAnimation={skipAnimation}
      onSelectDataPoint={onSelectDataPoint}
    />
  );
}

/**
 * Horizontal bars already read as a labelled list down the left edge, so they skip
 * the axis title and rotation that a bottom category axis needs.
 */
function InsightBarPlot({
  apiRef,
  colorDimension,
  model,
  onSelectDataPoint,
  secondaryLabel,
  skipAnimation,
}: InsightChartPlotProps) {
  return (
    <InsightBarsPlot
      apiRef={apiRef}
      colorDimension={colorDimension}
      layout="horizontal"
      model={model}
      secondaryLabel={secondaryLabel}
      skipAnimation={skipAnimation}
      onSelectDataPoint={onSelectDataPoint}
    />
  );
}

function InsightBarsPlot({
  apiRef,
  colorDimension,
  layout,
  model,
  onSelectDataPoint,
  primaryLabel,
  rotateCategoryLabels,
  secondaryLabel,
  skipAnimation,
}: InsightChartPlotProps & { layout: 'horizontal' | 'vertical' }) {
  const theme = useTheme();
  const palette = useMemo(() => rainbowSurgePalette(theme.palette.mode), [theme.palette.mode]);
  const handleItemClick = useCallback(
    (_event: MouseEvent, identifier: BarItemIdentifier) => {
      onSelectDataPoint?.({
        dataIndex: identifier.dataIndex,
        seriesId: String(identifier.seriesId),
      });
    },
    [onSelectDataPoint]
  );

  const horizontal = layout === 'horizontal';
  const categoryAxis = useMemo(
    () => ({ data: model.xLabels, scaleType: INSIGHT_AXIS_VALUES.band }),
    [model.xLabels]
  );
  const xAxis = useMemo(
    () =>
      horizontal
        ? [{ height: INSIGHT_AXIS_VALUES.auto, label: model.yLabel }]
        : [
            {
              ...categoryAxis,
              ...getCategoryAxisLabelConfig({
                label: primaryLabel,
                rotate: rotateCategoryLabels,
              }),
            },
          ],
    [categoryAxis, horizontal, model.yLabel, primaryLabel, rotateCategoryLabels]
  );
  const yAxis = useMemo(
    () =>
      horizontal
        ? [{ ...categoryAxis, width: INSIGHT_HORIZONTAL_AXIS_WIDTH_PX }]
        : [{ label: model.yLabel, width: INSIGHT_AXIS_VALUES.auto }],
    [categoryAxis, horizontal, model.yLabel]
  );
  const chartSeries = useMemo(() => {
    const visibleSeries = model.series.map((item, index) => {
      const { counts: _counts, ...plotItem } = item;
      return {
        ...plotItem,
        color: getSeriesColor(item.id, index, palette, colorDimension),
        layout,
        type: 'bar',
        valueFormatter: getInsightValueFormatter(model, item),
      } satisfies BarSeriesType;
    });
    if (!getHasTotalOverlay(model)) return visibleSeries;
    return [
      ...visibleSeries,
      {
        color: INSIGHT_CHART_TOTAL_SERIES_COLOR,
        data: model.totals.data,
        id: INSIGHT_CHART_TOTAL_SERIES_ID,
        label: getTotalSeriesLabel(model, secondaryLabel),
        showMark: false,
        type: 'line',
        valueFormatter: getTotalValueFormatter(model),
      } satisfies LineSeriesType,
    ];
  }, [colorDimension, layout, model, palette, secondaryLabel]);

  return (
    <InsightChartPlotShell
      apiRef={apiRef}
      height={getChartHeight(rotateCategoryLabels)}
      series={chartSeries}
      skipAnimation={skipAnimation}
      xAxis={xAxis}
      yAxis={yAxis}
    >
      <BarPlot onItemClick={onSelectDataPoint ? handleItemClick : undefined} />
      {getHasTotalOverlay(model) && <InsightChartTotalMarkers layout={layout} model={model} />}
      <ChartsXAxis />
      <ChartsYAxis />
      <InsightAxisTooltip />
    </InsightChartPlotShell>
  );
}

function InsightChartTotalMarkers({
  layout,
  model,
}: {
  layout: 'horizontal' | 'vertical';
  model: InsightChartModel;
}) {
  if (layout === 'horizontal') return <HorizontalTotalMarkers model={model} />;
  return <VerticalTotalMarkers model={model} />;
}

function HorizontalTotalMarkers({ model }: { model: InsightChartModel }) {
  const xScale = useXScale<'linear'>();
  const yScale = useYScale<'band'>();

  return (
    <g aria-label="Full totals across all series" pointerEvents="none">
      {model.totals.data.map((total, index) => {
        const label = model.xLabels[index];
        if (total === null || label === undefined) return null;
        const x = xScale(total);
        const yStart = yScale(label);
        if (x === undefined || yStart === undefined) return null;
        const y = yStart + yScale.bandwidth() / 2;
        return (
          <line
            key={model.xKeys[index] ?? label}
            stroke="#607D8B"
            strokeWidth={2}
            x1={x}
            x2={x}
            y1={y - 8}
            y2={y + 8}
          />
        );
      })}
    </g>
  );
}

function VerticalTotalMarkers({ model }: { model: InsightChartModel }) {
  const xScale = useXScale<'band'>();
  const yScale = useYScale<'linear'>();

  return (
    <g aria-label="Full totals across all series" pointerEvents="none">
      {model.totals.data.map((total, index) => {
        const label = model.xLabels[index];
        if (total === null || label === undefined) return null;
        const xStart = xScale(label);
        const y = yScale(total);
        if (xStart === undefined || y === undefined) return null;
        const x = xStart + xScale.bandwidth() / 2;
        return (
          <line
            key={model.xKeys[index] ?? label}
            stroke="#607D8B"
            strokeWidth={2}
            x1={x - 8}
            x2={x + 8}
            y1={y}
            y2={y}
          />
        );
      })}
    </g>
  );
}

function InsightLinePlot({
  apiRef,
  colorDimension,
  model,
  onSelectDataPoint,
  primaryLabel,
  rotateCategoryLabels,
  secondaryLabel,
  skipAnimation,
}: InsightChartPlotProps) {
  return (
    <InsightLinesPlot
      apiRef={apiRef}
      colorDimension={colorDimension}
      filled={false}
      model={model}
      primaryLabel={primaryLabel}
      rotateCategoryLabels={rotateCategoryLabels}
      secondaryLabel={secondaryLabel}
      skipAnimation={skipAnimation}
      onSelectDataPoint={onSelectDataPoint}
    />
  );
}

function InsightAreaPlot({
  apiRef,
  colorDimension,
  model,
  onSelectDataPoint,
  primaryLabel,
  rotateCategoryLabels,
  secondaryLabel,
  skipAnimation,
}: InsightChartPlotProps) {
  return (
    <InsightLinesPlot
      filled
      apiRef={apiRef}
      colorDimension={colorDimension}
      model={model}
      primaryLabel={primaryLabel}
      rotateCategoryLabels={rotateCategoryLabels}
      secondaryLabel={secondaryLabel}
      skipAnimation={skipAnimation}
      onSelectDataPoint={onSelectDataPoint}
    />
  );
}

function InsightLinesPlot({
  apiRef,
  colorDimension,
  filled,
  model,
  onSelectDataPoint,
  primaryLabel,
  rotateCategoryLabels,
  secondaryLabel,
  skipAnimation,
}: InsightChartPlotProps & { filled: boolean }) {
  const theme = useTheme();
  const palette = useMemo(() => rainbowSurgePalette(theme.palette.mode), [theme.palette.mode]);
  const handleMarkClick = useCallback(
    (_event: React.MouseEvent<SVGElement, MouseEvent>, identifier: LineItemIdentifier) => {
      if (identifier.dataIndex === undefined) return;
      if (identifier.seriesId === INSIGHT_CHART_TOTAL_SERIES_ID) return;
      onSelectDataPoint?.({
        dataIndex: identifier.dataIndex,
        seriesId: String(identifier.seriesId),
      });
    },
    [onSelectDataPoint]
  );

  const series = useMemo(() => {
    const visibleSeries = model.series.map((item, index) => {
      const { counts: _counts, ...plotItem } = item;
      return {
        ...plotItem,
        area: filled,
        color: getSeriesColor(item.id, index, palette, colorDimension),
        valueFormatter: getInsightValueFormatter(model, item),
      };
    });
    if (!getHasTotalOverlay(model)) return visibleSeries;
    return [
      ...visibleSeries,
      {
        area: false,
        color: INSIGHT_CHART_TOTAL_SERIES_COLOR,
        data: model.totals.data,
        id: INSIGHT_CHART_TOTAL_SERIES_ID,
        label: getTotalSeriesLabel(model, secondaryLabel),
        showMark: false,
        valueFormatter: getTotalValueFormatter(model),
      },
    ];
  }, [colorDimension, filled, model, palette, secondaryLabel]);
  const xAxis = useMemo(
    () => [
      {
        data: model.xLabels,
        scaleType: INSIGHT_AXIS_VALUES.point,
        ...getCategoryAxisLabelConfig({ label: primaryLabel, rotate: rotateCategoryLabels }),
      },
    ],
    [model.xLabels, primaryLabel, rotateCategoryLabels]
  );
  const yAxis = useMemo(
    () => [{ label: model.yLabel, width: INSIGHT_AXIS_VALUES.auto }],
    [model.yLabel]
  );

  return (
    <LineChartPro
      hideLegend
      apiRef={apiRef}
      height={getChartHeight(rotateCategoryLabels)}
      margin={INSIGHT_CHART_MARGIN}
      series={series}
      skipAnimation={skipAnimation}
      slots={INSIGHT_AXIS_TOOLTIP_SLOTS}
      xAxis={xAxis}
      yAxis={yAxis}
      onMarkClick={onSelectDataPoint ? handleMarkClick : undefined}
    />
  );
}

function InsightPiePlot({
  apiRef,
  colorDimension,
  model,
  onSelectDataPoint,
  skipAnimation,
}: InsightChartPlotProps) {
  const theme = useTheme();
  const palette = useMemo(() => rainbowSurgePalette(theme.palette.mode), [theme.palette.mode]);
  const data = useMemo(
    () => getInsightPieData({ colorDimension, model, palette }),
    [colorDimension, model, palette]
  );
  const handleItemClick = useCallback(
    (_event: React.MouseEvent<SVGPathElement, MouseEvent>, identifier: PieItemIdentifier) => {
      const sourceIndex = data.at(identifier.dataIndex)?.sourceIndex;
      if (typeof sourceIndex === 'undefined') return;
      onSelectDataPoint?.({ dataIndex: sourceIndex });
    },
    [data, onSelectDataPoint]
  );

  /**
   * A pie has no series axis, so only the first Measure is plotted. The Charts panel
   * caps pie Charts at one Dimension, which keeps the slices to the primary Dimension.
   */
  const series = useMemo(
    () => [
      {
        data,
        highlightScope: INSIGHT_PIE_HIGHLIGHT_SCOPE,
      },
    ],
    [data]
  );

  return (
    <PieChartPro
      apiRef={apiRef}
      height={INSIGHT_CHART_HEIGHT_PX}
      series={series}
      skipAnimation={skipAnimation}
      slots={INSIGHT_ITEM_TOOLTIP_SLOTS}
      onItemClick={onSelectDataPoint ? handleItemClick : undefined}
    />
  );
}

/**
 * A total overlay is only needed when the plotted stack cannot add up to the whole
 * cohort on its own — that is, when buckets are missing and no remainder series
 * accounts for them (an exact selection, or a Measure that cannot be summed).
 */
function getHasTotalOverlay(model: InsightChartModel): boolean {
  if (model.series.some(series => series.id === WORKFLOW_CHART_HIDDEN_SERIES_KEY)) return false;
  return model.secondaryCatalog.length > model.series.length;
}

export function getInsightPieData({
  colorDimension,
  model,
  palette,
}: {
  readonly colorDimension: string | undefined;
  readonly model: InsightChartModel;
  readonly palette: string[];
}) {
  return model.xLabels.flatMap((label, index) => {
    const value = model.series[0]?.data[index];
    if (value === null || typeof value === 'undefined') return [];
    const id = model.xKeys[index] ?? label;
    return [
      {
        color: getSeriesColor(id, index, palette, colorDimension),
        id,
        label,
        sourceIndex: index,
        value,
      },
    ];
  });
}

export function getSeriesColor(
  id: string,
  index: number,
  palette: string[],
  colorDimension?: string
): string {
  if (id === WORKFLOW_CHART_HIDDEN_SERIES_KEY) return INSIGHT_CHART_HIDDEN_SERIES_COLOR;
  if (colorDimension === WORKFLOW_CHART_DIMENSIONS.priority) {
    const priorityColor = PRIORITY_SERIES_COLORS[id];
    if (priorityColor) return priorityColor;
  }
  return palette[index % palette.length] ?? INSIGHT_CHART_HIDDEN_SERIES_COLOR;
}

function getTotalSeriesLabel(model: InsightChartModel, secondaryLabel = 'categories'): string {
  return `Total across all ${model.secondaryCatalog.length} ${secondaryLabel.toLocaleLowerCase()}`;
}

function getTotalValueFormatter(model: InsightChartModel) {
  return (value: number | null, context: { dataIndex: number }): string | null => {
    if (value === null) return null;
    if (model.yLabel !== WORKFLOW_CHART_MEASURE_LABELS.count) {
      const count = model.totals.counts[context.dataIndex] ?? 0;
      return `${formatInsightChartNumber(value, true)} total average · ${count} Workflows`;
    }

    const shown = model.series.reduce(
      (sum, series) => sum + (series.data[context.dataIndex] ?? 0),
      0
    );
    const hidden = Math.max(0, value - shown);
    return `${value} total · ${hidden} hidden`;
  };
}

export const INSIGHT_CHART_PLOTS = {
  [WORKFLOW_CHART_TYPES.area]: InsightAreaPlot,
  [WORKFLOW_CHART_TYPES.bar]: InsightBarPlot,
  [WORKFLOW_CHART_TYPES.column]: InsightColumnPlot,
  [WORKFLOW_CHART_TYPES.line]: InsightLinePlot,
  [WORKFLOW_CHART_TYPES.pie]: InsightPiePlot,
} as const satisfies Record<WorkflowChartType, (props: InsightChartPlotProps) => React.ReactNode>;
